import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { PiUserCircleDuotone } from 'react-icons/pi';
import { Skeleton } from '@v2/design-system';
import api from '@v2/lib/api';
import { useI18n } from '@v2/providers/I18nProvider';

type Row = {
    id: number;
    name: string;
    avatar_url?: string | null;
    current_rate_pct: number;
    user?: { name: string } | null;
    wallet: { earned_current_month: number; earned_total: number; balance: number; paid_out_total: number; forecast_total: number };
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

export default function GluteleaderInfluencersSection() {
    const { t } = useI18n();
    const query = useQuery({
        queryKey: ['gluteleader', 'beneficiaries'],
        queryFn: () => api.get('/commissioning/gluteleader/beneficiaries').then((r) => r.data.data as Row[]),
    });

    return (
        <section className="space-y-3">
            <div>
                <p className="text-base font-bold text-gray-900 dark:text-zinc-50">{t('v2.gluteleader_wallet.section_title')}</p>
                <p className="text-xs text-gray-500">{t('v2.gluteleader_wallet.section_hint')}</p>
            </div>
            {query.isLoading && <Skeleton className="h-32 w-full rounded-xl" />}
            <div className="grid gap-4 md:grid-cols-2">
                {(query.data ?? []).map((row) => (
                    <Link
                        key={row.id}
                        to={`/commissioning/gluteleader/beneficiaries/${row.id}`}
                        className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm transition-colors hover:border-primary dark:border-zinc-700 dark:bg-zinc-900"
                    >
                        <div className="flex items-center gap-3">
                            {row.avatar_url ? (
                                <img src={row.avatar_url} alt="" className="size-10 rounded-full object-cover" />
                            ) : (
                                <PiUserCircleDuotone className="size-10 text-gray-400" aria-hidden />
                            )}
                            <div className="min-w-0 flex-1">
                                <p className="truncate font-semibold">{row.name}</p>
                                <p className="text-xs text-gray-500">
                                    {row.current_rate_pct}% · {row.user ? t('v2.gluteleader_wallet.linked_user', { name: row.user.name }) : t('v2.gluteleader_wallet.no_user')}
                                </p>
                            </div>
                            <span className="text-xs font-semibold text-primary-dark">{t('v2.gluteleader_wallet.open_wallet')} →</span>
                        </div>
                        <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
                            {[
                                ['earned_month', row.wallet.earned_current_month],
                                ['earned_total', row.wallet.earned_total],
                                ['paid_out', row.wallet.paid_out_total],
                                ['balance', row.wallet.balance],
                            ].map(([key, value]) => (
                                <div key={key as string}>
                                    <dt className="text-[10px] font-semibold uppercase text-gray-500">{t(`v2.gluteleader_wallet.${key}`)}</dt>
                                    <dd className="font-bold tabular-nums">{brl(value as number)}</dd>
                                </div>
                            ))}
                        </dl>
                    </Link>
                ))}
            </div>
        </section>
    );
}
